import { deferTracking, sendGAEvent } from '@noths/polaris-client-google-analytics';
import type { EventsMap } from '@noths/polaris-client-next-redux';
import { getDeliveryByEventLabel } from '@noths/polaris-client-utils';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import {
  selectDeliveryOptionsForZone,
  selectMinMaxDeliveryEstimates,
} from 'src/redux/delivery/selectors';
import { selectDeliveryByEventCost } from 'src/redux/delivery/selectors/selectDeliveryByEventCost';
import { requestDeliveryZoneOptions } from 'src/redux/delivery/thunks';
import { isDeliveryAvailableByEvent } from 'src/redux/delivery/utils/isDeliveryAvailableByEvent';
import { selectProductDeliveryByEvent } from 'src/redux/product/selectors';
import { actions } from '../slice';
import type {
  DeliveryAreaLocationSelection,
  DeliveryInfoEvent,
  DeliveryInfoEventUnavailableDelivery,
} from './events';

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [actions.setSelectedCountry.type]: (state, nextState, action) => {
    const { payload } = action as ReturnType<typeof actions.setSelectedCountry>;

    if (typeof payload === 'object') {
      const { previouslySelectedZone, selectedCountry } = state.delivery;
      const fromLocation = `${selectedCountry} - ${previouslySelectedZone}`;
      const toLocation = `${payload.selectedCountry} - ${payload.selectedZone}`;
      const deliveryByEventCheapestPrice = selectDeliveryByEventCost(nextState);
      const productDeliveryByEvent = selectProductDeliveryByEvent(nextState);

      const deliveryEventIsAvailableForZone = Boolean(deliveryByEventCheapestPrice !== 'not set');
      const deliveryByEvent =
        deliveryEventIsAvailableForZone && productDeliveryByEvent?.arriveByEvent
          ? getDeliveryByEventLabel(productDeliveryByEvent.eventName).replace(' for', '')
          : 'not set';

      const countryDeliveryOptionCodes = selectDeliveryOptionsForZone(nextState)
        .map((opt) => opt.optionCode)
        .join(', ');

      const { max: deliveryOptionMax, min: deliveryOptionMin } =
        selectMinMaxDeliveryEstimates(nextState);

      deferTracking(() =>
        sendGAEvent<DeliveryAreaLocationSelection>({
          event: 'custom_event',
          event_action: 'Change delivery location',
          event_category: 'Delivery options',
          event_label: `FROM: ${fromLocation} TO: ${toLocation}`,
          delivery: {
            options: countryDeliveryOptionCodes,
            min_delivery_time: deliveryOptionMin,
            max_delivery_time: deliveryOptionMax,
            delivery_by_event: deliveryByEvent,
            delivery_by_event_cost: String(deliveryByEventCheapestPrice),
          },
        }),
      );
    }
  },
  [requestDeliveryZoneOptions.fulfilled.type]: (_, nextState) => {
    const countryDeliveryOptions = selectDeliveryOptionsForZone(nextState);

    if (!countryDeliveryOptions.length) {
      const { selectedCountry, selectedZone } = nextState.delivery;

      deferTracking(() =>
        sendGAEvent<DeliveryInfoEventUnavailableDelivery>({
          event: 'custom_event',
          event_action: 'Delivery info',
          event_category: 'Delivery & returns',
          event_label: `${selectedCountry} - ${selectedZone}`,
          delivery: {
            delivery_by_event: 'not set',
            delivery_by_event_cost: 'not set',
            max_delivery_time: 'not set',
            min_delivery_time: 'not set',
            options: 'none',
          },
        }),
      );
    } else {
      const countryDeliveryOptionCodesWithDeliveryProvider = countryDeliveryOptions
        .map((opt) => `${opt.optionCode} ${opt.providerName}`)
        .join(', ');

      const { max: deliveryOptionMax, min: deliveryOptionMin } =
        selectMinMaxDeliveryEstimates(nextState);

      const deliveryByEventCheapestPrice = selectDeliveryByEventCost(nextState);
      const productDeliveryByEvent = selectProductDeliveryByEvent(nextState);
      const deliveryEventIsAvailableForZone = Boolean(deliveryByEventCheapestPrice !== 'not set');
      const deliveryByEvent = isDeliveryAvailableByEvent(
        deliveryEventIsAvailableForZone,
        productDeliveryByEvent,
      )
        ? getDeliveryByEventLabel(productDeliveryByEvent!.eventName).replace(' for', '')
        : 'not set';

      deferTracking(() =>
        sendGAEvent<DeliveryInfoEvent>({
          event: 'custom_event',
          event_action: 'Delivery info',
          event_category: 'Delivery & returns',
          event_label: countryDeliveryOptionCodesWithDeliveryProvider,
          delivery: {
            options: countryDeliveryOptionCodesWithDeliveryProvider,
            min_delivery_time: deliveryOptionMin,
            max_delivery_time: deliveryOptionMax,
            delivery_by_event: deliveryByEvent,
            delivery_by_event_cost: String(deliveryByEventCheapestPrice),
          },
        }),
      );
    }
  },
};
